interface KeyboardShortcutsHelpProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function KeyboardShortcutsHelp({ isOpen, onClose }: KeyboardShortcutsHelpProps) {
  if (!isOpen) return null;

  const shortcuts = [
    {
      category: 'ページ移動',
      icon: 'ri-navigation-line',
      color: 'from-teal-500 to-emerald-500',
      items: [
        { keys: ['Alt', 'H'], description: 'ホームページへ移動' },
        { keys: ['Alt', 'D'], description: 'ダッシュボードへ移動' },
        { keys: ['Alt', 'Q'], description: 'クイズページへ移動' },
        { keys: ['Alt', 'P'], description: '研究経過報告へ移動' },
        { keys: ['Alt', 'N'], description: '勉強日記へ移動' },
        { keys: ['Alt', 'M'], description: '学習資料へ移動' },
        { keys: ['Alt', 'V'], description: '講義動画へ移動' },
      ],
    },
    {
      category: 'クイズ操作',
      icon: 'ri-questionnaire-line',
      color: 'from-amber-500 to-orange-500',
      items: [
        { keys: ['1', '2', '3', '4'], description: '選択肢を選ぶ' },
        { keys: ['Enter'], description: '次の問題へ / 回答する' },
        { keys: ['Esc'], description: 'クイズを終了' },
      ],
    },
    {
      category: 'その他',
      icon: 'ri-question-line',
      color: 'from-sky-500 to-cyan-500',
      items: [
        { keys: ['?'], description: 'このヘルプを表示' },
      ],
    },
  ];

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-teal-500 to-emerald-600 px-6 py-5 flex items-center justify-between z-10 rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
              <i className="ri-keyboard-line text-xl text-white"></i>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">キーボードショートカット</h2>
              <p className="text-xs text-teal-100">素早く操作して効率アップ</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-white/20 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl text-white"></i>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {shortcuts.map((section, idx) => (
            <div key={idx} className="space-y-3">
              <div className="flex items-center gap-2.5">
                <div className={`w-8 h-8 bg-gradient-to-br ${section.color} rounded-lg flex items-center justify-center`}>
                  <i className={`${section.icon} text-white text-sm`}></i>
                </div>
                <h3 className="text-base font-bold text-gray-900">{section.category}</h3>
              </div>
              <div className="space-y-2">
                {section.items.map((item, itemIdx) => (
                  <div
                    key={itemIdx}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                  >
                    <span className="text-sm text-gray-700">{item.description}</span>
                    <div className="flex items-center gap-1.5">
                      {item.keys.map((key, keyIdx) => (
                        <span key={keyIdx} className="flex items-center gap-1">
                          <kbd className="px-2.5 py-1 bg-white border border-gray-300 rounded-lg text-xs font-semibold text-gray-700 shadow-sm min-w-[32px] text-center">
                            {key}
                          </kbd>
                          {keyIdx < item.keys.length - 1 && (
                            <span className="text-gray-400 text-xs">+</span>
                          )}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Footer Note */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
            <div className="w-8 h-8 bg-amber-400 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-lightbulb-line text-white text-sm"></i>
            </div>
            <div>
              <p className="text-sm font-semibold text-amber-900 mb-1">ヒント</p>
              <p className="text-xs text-amber-700 leading-relaxed">
                入力フィールド（テキストボックスなど）にフォーカスしている時は、ショートカットは無効になります。
                クイズのショートカットはクイズページでのみ有効です。
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-between bg-gray-50 rounded-b-2xl">
          <p className="text-xs text-gray-500">
            <i className="ri-information-line mr-1"></i>
            いつでも <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-[10px] font-semibold">?</kbd> キーでこのヘルプを表示できます
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            閉じる
          </button>
        </div>
      </div>
    </div>
  );
}
